/**
 * Session facilitation copilot — Slice 2 of the AI platform brief.
 *
 * Answers "where does this session stand, what does it allow right now, and
 * how do I run it well?" from the session packet, on the layered instruction
 * stack. Strictly judgment-lane: the copilot never acts, scores, overrides,
 * or ranks — suggestedDrafts are plain text the facilitator may copy and send
 * themselves. Early-ranking asks are refused by naming the incomplete stages
 * (refusal matrix §2 S6 / §6).
 *
 * Registry: session_copilot — embedded advisor. Transport: callFeatureLlm
 * (graceful null when no provider is reachable — the UI shows a quiet
 * fallback; nothing user-blocking depends on the model being up).
 */

import { callFeatureLlm } from './llmClient';
import { isAiCapabilityEnabled } from './ai/aiAssistSettings';
import { buildInstructionStack } from './ai/instructionStack';
import { SESSION_COPILOT_CARD } from './ai/agentCard';
import { retrieveCanonSections } from './ai/canonRetrieval';
import {
  serializePacket,
  withCanonSections,
  type SessionPacket,
} from './ai/contextPacket';

export interface SessionCopilotDraft {
  label: string;
  text: string;
}

export interface SessionCopilotResult {
  reply: string;
  /** Optional copyable text drafts — never actions. */
  suggestedDrafts: SessionCopilotDraft[];
}

/** Capability appendix — the JSON output contract at point of use. */
export const SESSION_COPILOT_APPENDIX = `RESPONSE FORMAT
Respond with a single JSON object and nothing else:
{ "reply": "1-3 short paragraphs", "suggestedDrafts": [{ "label": "...", "text": "..." }] }
- "reply" answers the question from the session packet, citing packet facts by their refs.
- "suggestedDrafts" is OPTIONAL (omit it or use [] when nothing is worth drafting): short plain-text
  drafts the facilitator may copy and send themselves — e.g. a pause note, a divergence-discussion
  framing, a clarification prompt. Drafts are text only; they are never actions, scores, or rankings.
- If asked to rank (or hint at an ordering) while the packet's frame summary shows incomplete
  stages, refuse: name the incomplete stages and point to the next one as the legitimate path.`;

/** Parse the copilot JSON defensively (same idiom as llmHarryService). */
export function parseSessionCopilotJson(raw: string): SessionCopilotResult | null {
  const trimmed = raw.trim();
  const jsonBlock = trimmed.match(/```(?:json)?\s*([\s\S]*?)```/)?.[1] ?? trimmed;
  try {
    const parsed = JSON.parse(jsonBlock) as { reply?: unknown; suggestedDrafts?: unknown };
    const reply = typeof parsed.reply === 'string' ? parsed.reply.trim() : '';
    const suggestedDrafts = Array.isArray(parsed.suggestedDrafts)
      ? parsed.suggestedDrafts
          .filter(
            (d): d is { label: string; text: string } =>
              !!d &&
              typeof d === 'object' &&
              typeof (d as { label?: unknown }).label === 'string' &&
              typeof (d as { text?: unknown }).text === 'string',
          )
          .map((d) => ({ label: d.label.trim(), text: d.text.trim() }))
          .filter((d) => d.label.length > 0 && d.text.length > 0)
      : [];
    if (!reply) return null;
    return { reply, suggestedDrafts };
  } catch {
    return null;
  }
}

/**
 * Ask the session copilot a question about the session the packet describes.
 * Returns null when the capability is disabled, no provider is reachable, or
 * the model returns nothing usable — callers render a quiet fallback.
 */
export async function askSessionCopilot(params: {
  packet: SessionPacket;
  question: string;
  uiMode?: 'b' | 'c' | null;
}): Promise<SessionCopilotResult | null> {
  const question = params.question.trim();
  if (!question || !isAiCapabilityEnabled('session_copilot')) return null;

  // Canon packet: retrieval is query-dependent and non-fatal — an empty
  // result leaves the packet's named omission in place.
  const canonSections = await retrieveCanonSections(question).catch(() => []);
  const packet = withCanonSections(params.packet, canonSections);

  const { systemPrompt } = buildInstructionStack({
    card: SESSION_COPILOT_CARD,
    uiMode: params.uiMode ?? packet.uiMode,
    capabilityAppendix: SESSION_COPILOT_APPENDIX,
  });

  const content = await callFeatureLlm({
    userPrompt: `${serializePacket(packet)}\n\n${question}`,
    systemPrompt,
    conversationId: `session-copilot-${packet.session.id}`,
    maxTokens: 900,
    temperature: 0.4,
  });
  if (!content) return null;

  const parsed = parseSessionCopilotJson(content);
  if (parsed) return parsed;

  // Non-JSON but non-empty reply: keep the text, drop drafts.
  const fallback = content.trim();
  return fallback ? { reply: fallback, suggestedDrafts: [] } : null;
}
